import CardKit from "@/shared/components/kit/card";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { Grid } from "@mui/material";
import { walletCardsData } from "./data.totalAssets";
import StackKit from "@/shared/components/kit/stack";
import CustomButton from "@/shared/components/kit/button";
import { ListMerchantData } from "@/types/api/merchant/merchant.type";
import { persianToEnglishDigits } from "@/hooks/persianToEnglishDigits";
import RadialBarChart from "./radialChart";
import { toPersianNumber } from "@/hooks/useThousandsSeparator";
import { useNavigate } from "react-router";

/**
 * TotalAssets
 *
 * نمایش مجموعه کارت‌های دارایی پذیرنده شامل موجودی نقدی، هدیه، اعتبارات، و سایر انواع سرمایه.
 * این کامپوننت داده‌های مالی را از `walletCardsData` دریافت کرده و برای هر مورد یک کارت واکنش‌گرا رندر می‌کند.
 *
 * ویژگی‌ها:
 * - رندر کارت‌های دارایی با آیکون، مقدار، دکمه و نمودار دایره‌ای در صورت نیاز.
 * - محاسبه مقدار عددی برای نمودار با تبدیل ارقام فارسی به انگلیسی و بالعکس.
 * - طراحی کاملاً واکنش‌گرا با استفاده از Grid و Stack.
 * - پشتیبانی از لینک، آیکون لینک، دکمه عملیاتی و نمایش نمودار پیشرفت (RadialBarChart).
 *
 * پارامترها:
 * @param dataAssets اطلاعات کامل دارایی‌های پذیرنده (اختیاری).
 *
 * @returns JSX.Element مجموعه کارت‌های دارایی با قابلیت نمایش خلاصه‌ای از وضعیت کیف پول و اعتبارات.
 */

function TotalAssets({ dataAssets, }: { dataAssets?: ListMerchantData | undefined; }) {
  const navigate = useNavigate();
  const cards = walletCardsData({ dataAssets });
  return (
    <>
      <Grid container spacing={2} sx={{ display: "flex", justifyContent: "center", paddingTop: "10px", width: "100%", }}>
        {cards.map((item) => {
          const amountStr = item?.amount.split(" ")[0];
          const cleaned = persianToEnglishDigits(amountStr.replace(/,/g, ""));
          const amountNumber: any = cleaned ? toPersianNumber(`${cleaned}`) : toPersianNumber(`${0}`);

          return (
            <Grid key={item.title} sx={{ display: "flex", justifyContent: "center", width: { sm: '30%', xs: '80%' } }}>
              <CardKit sx={{ backgroundColor: "#434343", color: "#fff", width: "100%", borderRadius: 3, display: "flex", flexDirection: "column", justifyContent: "space-between",
                 position: "relative", border: "1px solid #333", padding: { xs: "10px", sm: "12px", md: "16px" }, }}>
                <StackKit sx={{ width: "100%"}} alignItems="start">
                  {/* Top: Title + Icon */}
                  <StackKit direction="row" justifyContent="space-between" alignItems="center" gap={2} sx={{ width: "100%", flexWrap: "wrap" }}>
                    <StackKit direction="row" justifyContent="start" gap={1}>
                      {item.icon}
                      <TypoGraphyKit variant="body1" sx={{ fontSize: { xs: "13px", sm: "14px" } }}>
                        {item.title}
                      </TypoGraphyKit>
                    </StackKit>
                  </StackKit>

                  {/* Middle: Amount + Chart */}
                  <StackKit sx={{ display: "flex", justifyContent: "flex-end", width: "100%", flexGrow: 1, }}>
                    {item.hasChart ? (
                      <StackKit direction="row" sx={{ width: "100%", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", }} gap={2}>
                        <TypoGraphyKit variant="body1" sx={{ color: "#fff", fontSize: { xs: "20px", sm: "24px" }, }}>
                          {item.amount}
                        </TypoGraphyKit>
                        <StackKit sx={{ width: { xs: "60%", sm: "40%" }, position: "relative", top: { xs: "-10px", sm: "-20px" }, }}>
                          <RadialBarChart value={item?.chartValue} label={`${amountNumber}`} color="#A90000" />
                        </StackKit>
                      </StackKit>
                    ) : (
                      <TypoGraphyKit variant="h6" fontWeight="bold" sx={{ fontSize: { xs: "20px", sm: "24px" } }}>
                        {item.amount}
                      </TypoGraphyKit>
                    )}
                  </StackKit>

                  {/* Bottom: Optional Button */}
                  {item.buttonText && (
                    <StackKit sx={{ width: "100%", paddingBottom: "10px", mt: "auto" }} alignItems="end">
                      <CustomButton sx={{ height: "36px", width: { xs: "100%", sm: "133px" } }}
                        onClick={() => {
                          if (item.link) {
                            navigate(item.link);
                          }
                        }}
                      >
                        <TypoGraphyKit variant="body1">
                          {item.buttonText}
                        </TypoGraphyKit>
                      </CustomButton>
                    </StackKit>
                  )}
                </StackKit>
              </CardKit>
            </Grid>
          );
        })}
      </Grid>
    </>
  );
}

export default TotalAssets;
